import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, ic } from "../wizard-icons";
import {
  PRODUCT_TYPE_EN,
  PRODUCT_TYPE_KEY,
  suggestionsForType,
  type CatPair,
  type ProductTypeId,
} from "../wizard-data";
import {
  SUGGEST_TIMEOUT_MS,
  useCategorySuggestionsMutation,
  type CategorySuggestion,
} from "../../services/useCategorySuggestionsMutation";

const K = keys.quickSetup;
const CLOSE_SYMBOLE = 'x';

// Ghost placeholder count + plausible widths (design handoff: 3-4 ghosts at
// 80-130px when real labels aren't known ahead of the response).
const GHOST_WIDTHS = [96, 118, 84, 130];

// Minimum time the ghost shimmer stays visible before swapping to real chips /
// fallback, so a fast cache hit still reads as an animation instead of a
// flash (design handoff "never block" + "reads complete even at <1s").
const MIN_GHOST_MS = 600;

function usePrefersReducedMotion(): boolean {
  // Lazy init from matchMedia so the effect only reacts to later changes (no
  // synchronous setState on mount).
  const [reduce, setReduce] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduce(mq.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

/**
 * Animates its own height to fit its content so that chips wrapping to a new
 * row (or a whole block appearing/disappearing) makes everything below slide
 * smoothly instead of jumping. Measures the content via ResizeObserver;
 * `overflow` is only clipped while the height is mid-transition so focus rings
 * aren't cut at rest. No-ops under prefers-reduced-motion.
 */
function SmoothHeight({ children }: { children: ReactNode }): ReactElement {
  const reduce = usePrefersReducedMotion();
  const innerRef = useRef<HTMLDivElement>(null);
  const lastH = useRef<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [animating, setAnimating] = useState(false);

  useLayoutEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    const measure = () => {
      const next = el.offsetHeight;
      if (lastH.current !== undefined && next !== lastH.current) setAnimating(true);
      lastH.current = next;
      setHeight(next);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  if (reduce) return <div>{children}</div>;

  return (
    <div
      style={{
        height,
        overflow: animating ? "hidden" : "visible",
        transition: "height 0.3s cubic-bezier(0.2, 0.8, 0.3, 1)",
      }}
      onTransitionEnd={(e) => {
        if (e.propertyName === "height") setAnimating(false);
      }}
    >
      {/* flow-root so the inner blocks' bottom margins count toward the measured
          height (no margin-collapse through the measured wrapper). */}
      <div ref={innerRef} style={{ display: "flow-root" }}>
        {children}
      </div>
    </div>
  );
}

function toCatPair(s: CategorySuggestion): CatPair {
  return { en: s.nameEn, ar: s.nameAr };
}

// ── Categories step ──────────────────────────────────────────────
export function CategoriesStep({
  sellType,
  sellTypeOther,
  initial,
  onDone,
  onLoadingChange,
}: {
  sellType: ProductTypeId | null;
  sellTypeOther: string;
  initial: CatPair[];
  onDone: (cats: CatPair[]) => void;
  /** Reports the AI-suggestion loading state up to the shell so the
   *  QuestionScreen badge ✦ spins in place while generating. */
  onLoadingChange?: (loading: boolean) => void;
}): ReactElement {
  const t = useTranslations();
  const locale = useLocale();
  const disp = (c: CatPair) => (locale === "ar" ? c.ar : c.en);

  // The phrase sent to the AI suggestions endpoint: preset → canonical English
  // label (keeps the global cache locale-independent); freeform ("other") →
  // the seller's own trimmed typed text. Null/empty when there's nothing
  // usable yet (e.g. "other" selected but no text typed).
  const phrase = useMemo(() => {
    if (!sellType) return "";
    if (sellType === "other") return sellTypeOther.trim();
    return PRODUCT_TYPE_EN[sellType];
  }, [sellType, sellTypeOther]);

  const fallbackSuggestions = useMemo(() => suggestionsForType(sellType ?? null), [sellType]);

  const { mutateAsync, reset } = useCategorySuggestionsMutation();

  // `aiSuggestions`: null = not resolved yet (loading or not started);
  // [] = resolved to nothing (error/timeout/empty) → use curated fallback.
  const [aiSuggestions, setAiSuggestions] = useState<CatPair[] | null>(null);
  const [loading, setLoading] = useState(false);

  // Mirror the loading state up to the shell (drives the QuestionScreen badge
  // spin + status subhead) and clear it on unmount so the badge never stays
  // spinning after leaving the step.
  useEffect(() => {
    onLoadingChange?.(loading);
    return () => onLoadingChange?.(false);
  }, [loading, onLoadingChange]);

  const [cats, setCats] = useState<CatPair[]>(initial && initial.length ? initial : []);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);

  // Guards against re-firing the same phrase (e.g. re-renders) and aborts an
  // in-flight request on unmount/phrase-change.
  const firedForPhrase = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Nothing to suggest for yet (no sell type / empty freeform, e.g. "other"
    // selected but no text typed) — reset any prior request state so the
    // manual-only fallback UI shows with no ghosts/spinner.
    if (!phrase) {
      firedForPhrase.current = null;
      abortRef.current?.abort();
      // eslint-disable-next-line react-hooks/set-state-in-effect -- reset request state to match the externally-derived empty phrase (no sell type / empty freeform text)
      setLoading(false);
      setAiSuggestions((prev) => (prev === null ? prev : []));
      return;
    }

    if (firedForPhrase.current === phrase) return;
    firedForPhrase.current = phrase;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setAiSuggestions(null);
    reset();

    const startedAt = Date.now();
    const timeoutId = window.setTimeout(() => controller.abort(), SUGGEST_TIMEOUT_MS);

    // Gate applying the result on "is this still the active controller"
    // rather than on `controller.signal.aborted` — React StrictMode's dev-only
    // synchronous mount→cleanup→remount aborts this same controller as part
    // of the cleanup below even when the *real* (only) request is still the
    // one we want to apply. A stale-phrase abort always installs a *new*
    // controller in `abortRef.current` before this one settles, so comparing
    // identity correctly distinguishes "superseded by a new phrase" (discard)
    // from "StrictMode's harmless double-invoke cleanup" (still apply).
    mutateAsync({ phrase, signal: controller.signal })
      .then((result) => {
        if (abortRef.current !== controller) return;
        const resolved = (result.suggestions ?? []).map(toCatPair);
        const elapsed = Date.now() - startedAt;
        const wait = Math.max(0, MIN_GHOST_MS - elapsed);
        window.setTimeout(() => {
          if (abortRef.current !== controller) return;
          setAiSuggestions(resolved);
          setLoading(false);
        }, wait);
      })
      .catch(() => {
        // Never surface an error to the seller — degrade to the curated
        // fallback (preset) or manual-only (freeform).
        if (abortRef.current !== controller) return;
        setAiSuggestions([]);
        setLoading(false);
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
      });

    // Deliberately NOT aborting `controller` here. React 18 StrictMode (dev
    // only) synchronously runs mount → this cleanup → remount once; if this
    // cleanup aborted the controller, it would cancel the one real in-flight
    // fetch outright (the guard above prevents the remount from firing a
    // replacement request for the same phrase), so the seller would always
    // hit the timeout/error fallback in dev. A genuinely new phrase already
    // aborts the previous controller explicitly (a few lines up) before
    // creating this one; true unmount just lets the in-flight request finish
    // and be discarded by the "is this still current" checks above.
    return () => {
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fires once per resolved phrase; mutateAsync/reset are stable
  }, [phrase]);

  // Real chips once resolved; while still loading show nothing here — ghosts
  // render separately. On resolved-empty, fall back to curated suggestions
  // for a preset sell type, or nothing for freeform.
  const suggestions = useMemo(() => {
    if (aiSuggestions === null) return [];
    if (aiSuggestions.length > 0) return aiSuggestions;
    if (sellType && sellType !== "other") return fallbackSuggestions;
    return [];
  }, [aiSuggestions, sellType, fallbackSuggestions]);

  const has = (c: CatPair) => cats.some((x) => x.en.toLowerCase() === c.en.toLowerCase());
  const toggle = (c: CatPair) => {
    setError(null);
    setCats((s) => (has(c) ? s.filter((x) => x.en !== c.en) : [...s, c]));
  };
  const addDraft = () => {
    const v = draft.trim();
    if (v && !cats.some((c) => c.en.toLowerCase() === v.toLowerCase() || c.ar === v)) {
      setError(null);
      setCats((s) => [...s, { ar: v, en: v }]);
    }
    setDraft("");
  };
  const remove = (c: CatPair) => setCats((s) => s.filter((x) => x.en !== c.en));

  const handleContinue = () => {
    if (cats.length === 0) {
      setError(t(K.errors.categories));
      return;
    }
    setError(null);
    onDone(cats);
  };

  return (
    <div style={{ animation: "qs-in .3s ease" }}>
      <SmoothHeight>
        {cats.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
          {cats.map((c) => (
            <span
              key={c.en}
              className="qs-cs-chip-arrive"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "10px 16px",
                borderRadius: 999,
                background: "var(--navy)",
                color: "#fff",
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              {disp(c)}
              <button
                onClick={() => remove(c)}
                aria-label={t(K.categories.removeAria, { name: disp(c) })}
                style={{
                  display: "flex",
                  background: "transparent",
                  border: "none",
                  borderRadius: 6,
                  width: 16,
                  height: 16,
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#fff",
                  opacity: 0.6,
                  fontSize: 14,
                  lineHeight: 1,
                  paddingBottom: 2,
                }}
              >
                {CLOSE_SYMBOLE}
              </button>
            </span>
          ))}
        </div>
      )}

      {/* The "generating" affordance now lives in the QuestionScreen header —
          the badge ✦ spins in place and the subhead shows the wave-dot status
          line (per the handoff). Here we only render the ghost/real chips. */}
      {(loading || suggestions.length > 0) && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "var(--fine)",
              letterSpacing: "0.08em",
              textTransform: "uppercase",
            }}
          >
            {t(K.categories.suggestedFor, {
              // Echo the seller's actual answer: the typed freeform value for
              // "other" (never the literal "Something else"), else the preset
              // label. Matches the design ("SUGGESTED FOR SCENTED CANDLES").
              type:
                sellType === "other"
                  ? sellTypeOther.trim()
                  : sellType
                    ? t(PRODUCT_TYPE_KEY[sellType])
                    : "",
            })}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {loading
              ? GHOST_WIDTHS.map((w, i) => (
                  <span
                    key={i}
                    className="qs-cs-ghost"
                    style={{ width: w, animationDelay: `${i * 0.2}s` }}
                    aria-hidden="true"
                  >
                    &nbsp;
                  </span>
                ))
              : suggestions
                  .filter((s) => !has(s))
                  .map((s, i) => (
                    <button
                      key={s.en}
                      onClick={() => toggle(s)}
                      className="qs-cs-chip-arrive qs-cs-fade"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "10px 16px",
                        borderRadius: 999,
                        background: "var(--surface)",
                        border: "1.5px dashed var(--dashed)",
                        color: "var(--navy)",
                        fontSize: 14,
                        fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        animationDelay: `${i * 0.3}s`,
                      }}
                    >
                      {ic(Icons.plus, 14)} {disp(s)}
                    </button>
                  ))}
          </div>
        </div>
      )}
      </SmoothHeight>

      <div
        style={{
          display: "flex",
          gap: 10,
          marginBottom: 16,
          opacity: loading ? 0.45 : 1,
          pointerEvents: loading ? "none" : "auto",
          transition: "opacity .2s ease",
        }}
      >
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            border: "1.5px solid var(--border-strong)",
            borderRadius: 10,
            padding: "0 16px",
            height: 50,
            background: "var(--surface)",
            boxSizing: "border-box",
          }}
        >
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") addDraft();
            }}
            disabled={loading}
            placeholder={t(K.categories.addPlaceholder)}
            style={{
              flex: 1,
              minWidth: 0,
              border: "none",
              outline: "none",
              fontSize: 15,
              fontFamily: "inherit",
              color: "var(--text)",
              background: "transparent",
            }}
          />
        </div>
        <button
          onClick={addDraft}
          disabled={!draft.trim() || loading}
          style={{
            height: 50,
            padding: "0 22px",
            borderRadius: 10,
            border: "1.5px solid var(--navy)",
            background: "var(--surface)",
            color: "var(--navy)",
            fontSize: 14,
            fontWeight: 800,
            cursor: draft.trim() && !loading ? "pointer" : "not-allowed",
            fontFamily: "inherit",
            opacity: draft.trim() && !loading ? 1 : 0.5,
          }}
        >
          {t(K.categories.add)}
        </button>
      </div>

      {error && (
        <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: "var(--danger)" }}>
          {error}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          onClick={handleContinue}
          style={{
            width: "100%",
            padding: "13px 16px",
            borderRadius: 10,
            border: "none",
            background: "var(--accent)",
            color: "var(--navy)",
            fontSize: 16,
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: "inherit",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
          }}
        >
          {/* `continueOne`'s Arabic string hardcodes "one" ("قسم واحد") rather
           * than interpolating {count} — only pick it for an exact count of 1;
           * 0 (and 2+) must use the pluralized/interpolated `continueMany`
           * string so a fresh, untouched categories screen never claims "1
           * category" when none have been added yet. */}
          {t(cats.length === 1 ? K.categories.continueOne : K.categories.continueMany, {
            count: cats.length,
          })}
        </button>
        <span style={{ fontSize: 13, color: "var(--fine)", textAlign: "center" }}>
          {t(K.categories.reassurance)}
        </span>
      </div>
    </div>
  );
}
